import { useCallback } from "react";
import { useAuthConfig } from "../auth/authContext";
import { useRealtimeConnection, useRealtimeStateStore } from "../realtime/useConnectionState";
import {
    PeerNavigateAckSchema,
    RealtimeEvents,
    RealtimeHubMethods,
} from "../realtime/notifications";
import { openFallbackWindow } from "./openFallbackWindow";

/**
 * How long the sender waits for a designated Galileo tab to acknowledge a
 * navigation intent before falling back to opening a tab.
 *
 * Hard ceiling: the fallback is a `window.open` fired from this timer, and a
 * popup is only permitted while the click's transient user activation is still
 * live — ~5s in Chrome/Firefox. A timeout at or beyond that lifetime makes the
 * fallback fire with no activation, so the browser silently blocks the popup
 * and nothing opens. The value therefore stays well inside the window: long
 * enough for a healthy ack round trip (SignalR + two API relays, typically well
 * under a second), short enough that the fallback still counts as user-driven.
 * A genuinely slow relay may both fall back here and navigate the designated
 * tab in place; a duplicate tab on that rare path is preferable to a silently
 * blocked popup on every click.
 */
const ACK_TIMEOUT_MS = 2_500;

export interface UseGalileoNavigationResult {
    /**
     * Ask the user's designated Galileo tab to open `path` (an absolute,
     * origin-relative path such as "/loads/123"). If no designated tab
     * acknowledges within {@link ACK_TIMEOUT_MS}, falls back to opening the
     * path on the configured Galileo origin in a safe new tab.
     */
    readonly navigateToGalileo: (path: string) => void;

    /**
     * Open a producer-supplied absolute URL (a work item's
     * `externalActionUrl`). Dispatch is decided by origin, not trust: a URL
     * that resolves to the configured Galileo origin is relativized and handed
     * to {@link navigateToGalileo} so it reuses the in-place peer relay (no new
     * tab); any other origin opens in a fresh `_blank` tab with
     * `noopener,noreferrer` so a third-party system can neither reach back to
     * `window.opener` nor leak a referrer. A value that cannot be parsed as an
     * absolute URL is ignored — the server guarantees https/absolute at the
     * boundary, so this is a defensive no-op rather than an expected path.
     */
    readonly navigateExternal: (url: string) => void;
}

/**
 * Cross-app navigation sender (Einstein -> Galileo).
 *
 * Einstein and Galileo are separate origins, so the old same-origin
 * BroadcastChannel relay is dead. Instead the request is server-mediated: we
 * invoke the realtime hub's `RequestPeerNavigation`, the Einstein API forwards
 * it to the Galileo API addressed to the same user, and Galileo pushes it to
 * the user's designated tab which navigates in place and acks. The ack returns
 * here as a `PeerNavigateAck`; receiving it cancels the fallback so no new tab
 * opens. If the connection is down, the invoke fails, or no ack arrives in
 * time, we fall back to a new, opener-isolated tab against `galileoOrigin`.
 */
export function useGalileoNavigation(): UseGalileoNavigationResult {
    const { galileoOrigin } = useAuthConfig();
    const connection = useRealtimeConnection();
    const stateStore = useRealtimeStateStore();

    const navigateToGalileo = useCallback(
        (path: string) => {
            // `galileoOrigin` is validated as an absolute URL at boot, so the
            // URL constructor never throws on a configured app; it also
            // normalizes any trailing-slash mismatch with the leading-slash path.
            const fallbackUrl = new URL(path, galileoOrigin).toString();
            const openFallback = (): void => {
                openFallbackWindow(fallbackUrl);
            };

            // No live connection: there is no designated tab to reach, so open
            // immediately rather than waiting out the timeout for nothing.
            if (connection === null || stateStore.getSnapshot() !== "connected") {
                openFallback();
                return;
            }

            const correlationId =
                typeof crypto !== "undefined" && "randomUUID" in crypto
                    ? crypto.randomUUID()
                    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

            let settled = false;

            // `settle` references `handleAck` and `timeoutId` declared below;
            // both exist by the time it is ever invoked (after the listener and
            // timer are wired), so the forward reference is safe at runtime.
            const settle = (fallback: boolean): void => {
                if (settled) return;
                settled = true;
                clearTimeout(timeoutId);
                connection.off(RealtimeEvents.peerNavigateAck, handleAck);
                if (fallback) openFallback();
            };

            const handleAck = (payload: unknown): void => {
                const parsed = PeerNavigateAckSchema.safeParse(payload);
                if (!parsed.success || parsed.data.correlationId !== correlationId) {
                    // Not our ack (another in-flight intent, or malformed) —
                    // keep listening until our own ack or the timeout.
                    return;
                }
                settle(false);
            };

            connection.on(RealtimeEvents.peerNavigateAck, handleAck);
            const timeoutId = setTimeout(() => settle(true), ACK_TIMEOUT_MS);

            connection
                .invoke(RealtimeHubMethods.requestPeerNavigation, path, correlationId)
                .catch(() => {
                    // The relay never started; don't wait out the timeout.
                    settle(true);
                });
        },
        [connection, stateStore, galileoOrigin],
    );

    const navigateExternal = useCallback(
        (url: string) => {
            let target: URL;
            try {
                target = new URL(url);
            } catch {
                // Server validates absolute/https at the boundary; an
                // unparseable value here means a corrupt payload. Do nothing
                // rather than feed a bad string to window.open.
                return;
            }

            // Same-origin (a link back into Galileo) rides the in-place peer
            // relay: relativize to path+query+hash and reuse navigateToGalileo
            // so the user's existing Galileo tab navigates rather than a new
            // one opening. Origin comparison is scheme+host+port exact — the
            // same test the browser uses — so an http downgrade or a look-alike
            // host falls through to the new-tab branch.
            const galileoRoot = new URL(galileoOrigin);
            if (target.origin === galileoRoot.origin) {
                navigateToGalileo(`${target.pathname}${target.search}${target.hash}`);
                return;
            }

            // Foreign origin: open in a fresh tab, severed from this one.
            window.open(url, "_blank", "noopener,noreferrer");
        },
        [navigateToGalileo, galileoOrigin],
    );

    return { navigateToGalileo, navigateExternal };
}
